import React, { useEffect, useState } from "react";
import axios from "axios";

const MsplApprovals = ({ switchToTab }) => {
  const [data, setData] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [loading, setLoading] = useState({}); // Track loading state for each button
  const [responseMessage, setResponseMessage] = useState(null); // Store API response message and count
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchApprovalData = async () => {
      try {
        const projectId = localStorage.getItem("selectedProject");
        const response = await axios.get(`${baseURL}get_pdi_witness`, {
          params: { projectId },
        });
        setData(response.data.data);
      } catch (error) {
        console.error("Error fetching approval data:", error);
      }
    };
    
    fetchApprovalData();
  }, []);

  const handleAction = async (id, action) => {
    setLoading((prev) => ({ ...prev, [id]: action }));
    try {
      const uniqueId = data.find((entry) => entry.id === id).uniqueId;
      
      const response = await axios.post(`${baseURL}update_pdi_mspl_approval`, {
        projectId: localStorage.getItem("selectedProject"),
        fileName: uniqueId,
        mspl_approval: action,
      });

      const statusMap = {
        approved: "Approved",
        rejected: "Rejected",
        accepted_on_deviation: "Accepted on Deviation",
      };
  
      // Store the API response message and PDIs Approved Count
      setResponseMessage({
        message: response.data.message,
        pdiApprovedCount: response.data.pdi_updated_count,
      });

      setTimeout(() => {setResponseMessage(null);}, 20000);

      setData((prevData) =>
        prevData.map((entry) =>
          entry.id === id
            ? { ...entry, mspl_status: statusMap[action] || entry.mspl_status }
            : entry
        )
      );
    } catch (error) {
      console.error("Error updating MSPL status:", error);
      setResponseMessage({
        message: "Failed to update MSPL status.",
        pdiApprovedCount: 0,
      });
      setTimeout(() => {
        setResponseMessage(null);
      }, 5000);
    } finally {
      setLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const handleFileSelect = (id, files) => {
    setSelectedFiles((prevFiles) => ({
      ...prevFiles,
      [id]: files,
    }));
  };

  const handleFileUpload = async (entry) => {
    setLoading((prev) => ({ ...prev, [entry.id]: "upload" }));
    const { uniqueId, supplierName } = entry;
    const projectId = localStorage.getItem("selectedProject");
    const files = selectedFiles[entry.id];
  
    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append("projectId", projectId);
      formData.append("supplierName", supplierName);
      formData.append("uniqueId", uniqueId);
  
      Array.from(files).forEach((file) => formData.append("documents", file));
  
      try {
        await axios.post(`${baseURL}upload_mspl_document`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        alert("Files uploaded successfully!");
        setSelectedFiles((prevFiles) => ({ ...prevFiles, [entry.id]: [] }));
      } catch (error) {
        console.error("Error uploading documents:", error);
        alert("Failed to upload files.");
      } finally {
        setLoading((prev) => ({ ...prev, [entry.id]: null }));
      }
    } else {
      alert("Please select files to upload.");
    }
  };
    
  const handleViewPDF = (uniqueId) => {
    localStorage.setItem("viewUniqueId", uniqueId);
    if (switchToTab) {
      switchToTab(2);
    }
    setTimeout(() => {
      localStorage.removeItem("viewUniqueId");
    }, 5000);
  };

  return (
    <div className="p-4 bg-gray-200">
      <h2 className="text-xl font-bold mb-4">MSPL Approvals</h2>
      {responseMessage && (
        <div className="mb-4 p-4 bg-blue-100 text-blue-800 rounded-lg shadow-md">
          <p>{responseMessage.message}</p>
          <p><strong>PDIs Approved Count:</strong> {responseMessage.pdiApprovedCount}</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full bg-white shadow-md rounded-lg overflow-hidden">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="p-3 text-left whitespace-nowrap">Unique ID</th>
              <th className="p-3 text-left whitespace-nowrap">Supplier Name</th>
              <th className="p-3 text-left whitespace-nowrap">Date</th>
              <th className="p-3 text-left whitespace-nowrap">PDI Review Status</th>
              <th className="p-3 text-left whitespace-nowrap">MSPL Status</th>
              <th className="p-3 text-left whitespace-nowrap">Actions</th>
              <th className="p-3 text-left whitespace-nowrap">Upload Files</th>
              <th className="p-3 text-left whitespace-nowrap">View</th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((entry) => (
                <tr key={entry.id} className="border-b hover:bg-gray-100">
                  <td className="p-3 whitespace-nowrap">{entry.uniqueId}</td>
                  <td className="p-3 whitespace-nowrap">{entry.supplierName}</td>
                  <td className="p-3 whitespace-nowrap">{entry.date}</td>
                  <td className="p-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded ${
                        entry.status === "approved"
                          ? "bg-green-200 text-green-800"
                          : "bg-red-200 text-red-800"
                      }`}
                    >
                      {entry.status}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded ${
                        entry.mspl_status === "Approved"
                          ? "bg-green-200 text-green-800"
                          : entry.mspl_status === "Rejected"
                          ? "bg-red-200 text-red-800"
                          : entry.mspl_status === "Accepted on Deviation"
                          ? "bg-yellow-200 text-yellow-800"
                          : "bg-gray-200 text-gray-800"
                      }`}
                    >
                      {entry.mspl_status || "Pending"}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <button
                      className="bg-green-500 text-white py-1 px-3 rounded mr-2 relative disabled:opacity-50"
                      onClick={() => handleAction(entry.id, "approved")}
                      disabled={loading[entry.id] != null}
                    >
                      {loading[entry.id] === "approved" ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin h-5 w-5 mr-2 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading...
                        </span>
                      ) : (
                        "Approve"
                      )}
                    </button>
                    <button
                      className="bg-red-500 text-white py-1 px-3 rounded mr-2 relative disabled:opacity-50"
                      onClick={() => handleAction(entry.id, "rejected")}
                      disabled={loading[entry.id] != null}
                    >
                      {loading[entry.id] === "rejected" ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin h-5 w-5 mr-2 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading...
                        </span>
                      ) : (
                        "Reject"
                      )}
                    </button>
                    <button
                      className="bg-yellow-500 text-white py-1 px-3 rounded relative disabled:opacity-50"
                      onClick={() => handleAction(entry.id, "accepted_on_deviation")}
                      disabled={loading[entry.id] != null}
                    >
                      {loading[entry.id] === "accepted_on_deviation" ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin h-5 w-5 mr-2 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading...
                        </span>
                      ) : (
                        "Accept on Deviation"
                      )}
                    </button>
                  </td>
                  <td className="p-0 whitespace-nowrap">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleFileSelect(entry.id, e.target.files)}
                      className="bg-gray-200 rounded-l-md border"
                    />
                    <button
                      className="bg-[#424752] text-white py-1 px-3 rounded-r-md relative disabled:opacity-50"
                      onClick={() => handleFileUpload(entry)}
                      disabled={loading[entry.id] != null}
                    >
                      {loading[entry.id] === "upload" ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin h-5 w-5 mr-2 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading...
                        </span>
                      ) : (
                        "Upload"
                      )}
                    </button>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <button
                      className="bg-[#424752] text-white py-1 px-3 rounded"
                      onClick={() => handleViewPDF(entry.uniqueId)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="p-3 text-center text-gray-500">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MsplApprovals;